/**
 * @fileoverview User Activity Detection Hook
 *
 * Monitors user activity (mouse, keyboard, touch, scroll) and provides
 * a way to pause background operations when the user is inactive.
 * This helps reduce unnecessary server load when users are idle.
 *
 * @example
 * const { isActive, lastActivity } = useUserActivity(10 * 60 * 1000); // 10 minutes
 *
 * useEffect(() => {
 *   if (!isActive) {
 *     // Pause background requests
 *   }
 * }, [isActive]);
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Default inactivity timeout in milliseconds (10 minutes)
 * @constant {number}
 */
const DEFAULT_INACTIVITY_TIMEOUT = 10 * 60 * 1000;

/**
 * Events that indicate user activity
 * @constant {string[]}
 */
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel'
];

/**
 * Throttle delay for activity events (prevents excessive updates)
 * @constant {number}
 */
const THROTTLE_DELAY = 1000; // 1 second

/**
 * Custom hook to detect user activity/inactivity
 *
 * @param {number} [inactivityTimeout=DEFAULT_INACTIVITY_TIMEOUT] - Time in ms before user is considered inactive
 * @returns {Object} Activity state
 * @returns {boolean} returns.isActive - Whether the user is currently active
 * @returns {number} returns.lastActivity - Timestamp of last user activity
 * @returns {function} returns.resetActivity - Function to manually reset activity timer
 */
export const useUserActivity = (inactivityTimeout = DEFAULT_INACTIVITY_TIMEOUT) => {
  const [isActive, setIsActive] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Refs for throttling and timeout management
  const lastActivityRef = useRef(Date.now());
  const throttleRef = useRef(null);
  const timeoutRef = useRef(null);

  /**
   * Reset activity timer and mark user as active
   */
  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
    setIsActive(true);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for inactivity
    timeoutRef.current = setTimeout(() => {
      setIsActive(false);
    }, inactivityTimeout);
  }, [inactivityTimeout]);

  /**
   * Handle user activity events (throttled)
   */
  const handleActivity = useCallback(() => {
    // Throttle activity updates
    if (throttleRef.current) return;

    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
    }, THROTTLE_DELAY);

    resetActivity();
  }, [resetActivity]);

  // Set up event listeners
  useEffect(() => {
    // Add event listeners for all activity events
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility change (tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial timeout setup
    resetActivity();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, [handleActivity, resetActivity]);

  return {
    isActive,
    lastActivity,
    resetActivity
  };
};

export default useUserActivity;
